import {Injectable} from '@angular/core';
import {sortBy} from 'lodash';
import {Reservation} from '../../models/reservation';
import {Option, SearchOptions} from '../models/search-options';
import {environment} from '../../../environments/environment';
import {MatDialog} from '@angular/material';
import {HttpClient} from '@angular/common/http';
import {AuthenticationService} from '../../authentication/shared/authentication.service';
import {Observable} from 'rxjs/Observable';
import {Params} from '@angular/router';

/**
 * Can search for pick up lists.
 */
@Injectable()
export class PickUpListService {
  private searchOptions?: Observable<SearchOptions>;

  constructor(private http: HttpClient, public dialog: MatDialog, private auth: AuthenticationService) {
  }

  /**
   * Search for reservations
   * @param query
   * @returns {Observable<T>}
   */
  search(query: Params): Observable<{ query: string, reservations: Reservation[] }> {
    return this.http.get<{ query: string, reservations: Reservation[] }>(
      `${environment.apiUrl}/reservation/search`,
      {headers: this.auth.getAuthHeaders(), params: query}
    )
      .do(result => result.reservations = this.parseReservations(result.reservations));
  }

  sort(reservations: Reservation[]): Reservation[] {
    return sortBy(reservations, ['placementInfo', 'dk5', 'sortableAuthor', 'sortableTitle']);
  }

  sortByReservationAge(reservations: Reservation[]): Reservation[] {
    return sortBy(reservations, ['reservationAge', 'placementInfo', 'dk5', 'sortableAuthor', 'sortableTitle']);
  }

  /**
   * Gets all possible search options for the agency
   * @returns {Observable<SearchOptions>}
   */
  getSearchOptions(): Observable<SearchOptions> {
    if (this.searchOptions) {
      return this.searchOptions;
    }

    // Clear cache when it expires
    setTimeout(() => this.searchOptions = undefined, environment.searchOptionsCacheTimeMs);

    // Cache the HTTP response
    return this.searchOptions = this.http.get<SearchOptions>(
      `${environment.apiUrl}/reservation/searchOptions`,
      {headers: this.auth.getAuthHeaders()}
    )
      .map(options => this.parseSearchOptions(options))
      .publishReplay(1)
      .refCount();
  }

  updateReservationStates(reservations: Reservation[]) {
    return this.http
      .post<{ [id: number]: Reservation['state'] }>(
        `${environment.apiUrl}/reservation/getFound`,
        reservations.map(r => r.reservationId),
        {headers: this.auth.getAuthHeaders()}
      )
      .map(stateMap => {
        reservations
          .filter(reservation => !reservation.canUndo)
          .filter(reservation => reservation.state !== stateMap[reservation.reservationId])
          .forEach(reservation => {
            reservation.state = stateMap[reservation.reservationId];
            reservation.canUndo = false;
          });

        return reservations;
      });
  }

  /**
   * Parses reservations fetched from the BE, to the model we expect on the FE
   * @param reservations
   * @returns {any[]}
   */
  parseReservations(reservations: Reservation[]): Reservation[] {
    const dayInMs = 1000 * 60 * 60 * 24;

    reservations.forEach(reservation => {
      reservation.reservationDate = new Date(reservation.reservationDate);

      const daysOld = Math.floor(Date.now() / dayInMs) - Math.floor(reservation.reservationDate.getTime() / dayInMs);

      reservation.reservationAge = 1;
      if (daysOld > 2) {
        reservation.reservationAge = 2;
      }
      if (daysOld > 4) {
        reservation.reservationAge = 3;
      }

      if (reservation.title) {
        const indexOfSortingChar = reservation.title.indexOf('¤');

        if (indexOfSortingChar > -1) {
          reservation.sortableTitle = reservation.title.slice(indexOfSortingChar + 1);
          reservation.title = reservation.title.replace('¤', '');
        } else {
          reservation.sortableTitle = reservation.title;
        }
      }

      if (reservation.author) {
        const indexOfSortingChar = reservation.author.indexOf('¤');

        if (indexOfSortingChar > -1) {
          reservation.sortableAuthor = reservation.author.slice(indexOfSortingChar + 1);
          reservation.author = reservation.author.replace('¤', '');
        } else {
          reservation.sortableAuthor = reservation.author;
        }
      }
    });

    return reservations;
  }

  private parseSearchOptions(options: SearchOptions): SearchOptions {
    options.branches = sortBy(options.branches, 'shortName');

    options.placements.departments = sortBy(options.placements.departments, 'shortName');
    options.placements.locations = sortBy(options.placements.locations, 'shortName');
    options.placements.sublocations = sortBy(options.placements.sublocations, 'shortName');

    options.materialGroups = sortBy(options.materialGroups, [(materialGroup: Option) => materialGroup.name.toLowerCase()]);

    options.pickupBranches = sortBy(options.branches, 'shortName');

    return options;
  }
}



// WEBPACK FOOTER //
// ./src/app/pick-up-list/shared/pick-up-list.service.ts